import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { initials, STATUS_LABEL } from "../lib/format";
import type { ConversationStatus } from "../lib/types";

export function Avatar({ name, fallback }: { name: string | null; fallback: string }) {
  return (
    <span className="avatar" aria-hidden="true">
      {initials(name, fallback)}
    </span>
  );
}

export function StatusPill({ status }: { status: ConversationStatus }) {
  return <span className={`pill pill-${status}`}>{STATUS_LABEL[status]}</span>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

/** Placeholder rows shaped like the inbox list while it loads. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-list" role="status" aria-busy="true" aria-label="Cargando conversaciones">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <div key={i} className="skeleton-row">
          <span className="skeleton" style={{ width: `${55 + ((i * 17) % 30)}%` }} />
          <span className="skeleton" style={{ width: `${70 + ((i * 11) % 25)}%` }} />
        </div>
      ))}
    </div>
  );
}

/** Placeholder bubbles while a conversation loads. */
export function ChatSkeleton() {
  return (
    <div className="skeleton-chat" role="status" aria-busy="true" aria-label="Cargando mensajes">
      {["in", "out", "in", "out"].map((side, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <span key={i} className={`skeleton skeleton-bubble ${side}`} style={{ width: `${40 + ((i * 13) % 30)}%` }} />
      ))}
    </div>
  );
}

// ---- Toasts --------------------------------------------------------------

const ToastContext = createContext<(message: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const show = useCallback((text: string) => {
    setMessage(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), 3500);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      {message && (
        <div key={message} className="toast" role="status">
          {message}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// ---- Confirmations -------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Label of the destructive button, e.g. "Eliminar". */
  confirmLabel: string;
  cancelLabel?: string;
}

const ConfirmContext = createContext<(options: ConfirmOptions) => Promise<boolean>>(async () => false);

/** `await confirm({...})` resolves true only when the person presses the destructive button. */
export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  useEffect(() => {
    if (options && !dialog.current?.open) dialog.current?.showModal();
  }, [options]);

  const finish = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    dialog.current?.close();
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <dialog
        ref={dialog}
        className="confirm"
        aria-labelledby="confirm-title"
        onCancel={(e) => {
          e.preventDefault();
          finish(false);
        }}
      >
        {options && (
          <div className="dialog-form">
            <h3 id="confirm-title">{options.title}</h3>
            <p className="muted">{options.message}</p>
            <div className="row">
              {/* Focus starts on the safe choice, so Enter never deletes by accident. */}
              {/* biome-ignore lint/a11y/noAutofocus: the safe action takes focus when the dialog opens */}
              <button className="btn" type="button" autoFocus onClick={() => finish(false)}>
                {options.cancelLabel ?? "Cancelar"}
              </button>
              <button className="btn btn-danger-solid" type="button" onClick={() => finish(true)}>
                {options.confirmLabel}
              </button>
            </div>
          </div>
        )}
      </dialog>
    </ConfirmContext.Provider>
  );
}
